import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { faSquare } from '@fortawesome/pro-regular-svg-icons/faSquare';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons/faSquareCheck';
import clsx from 'clsx';
import { Label } from '../Label/Label';
import ClickAwayListener from 'react-click-away-listener';
import styles from './Select.module.scss';

export interface SelectOption<T> {
    label: string;
    value: string;
    data?: T;
}

export interface SelectProps<T> {
    label?: string;
    icon?: IconProp;
    error?: string;
    placeholder?: string;
    className?: string;
    triggerClassName?: string;
    options: Array<SelectOption<T>>;
    value?: string;
    disabled?: boolean;
    required?: boolean;
    renderOption?: (option: SelectOption<T>) => React.ReactNode;
    onChange?: (value: string, option: SelectOption<T>) => void;
    multiple?: boolean;
    values?: Array<string>;
    renderValues?: (options: Array<SelectOption<T>>) => React.ReactNode;
    onChangeMultiple?: (values: Array<string>, options: Array<SelectOption<T>>) => void;
}

export const Select = <T,>({
    options,
    className,
    triggerClassName,
    renderOption,
    value,
    onChange,
    placeholder,
    label,
    error,
    icon,
    disabled,
    required,
    multiple,
    values,
    renderValues,
    onChangeMultiple,
    ...props
}: SelectProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(option => option.value === value);
    const selectedOptions = multiple ? options.filter(option => values?.includes(option.value)) : [];

    const isOptionSelected = (option: SelectOption<T>) =>
        multiple ? values?.includes(option.value) ?? false : option.value === value;

    const handleOptionClick = (option: SelectOption<T>) => {
        if (multiple) {
            const newOptions = isOptionSelected(option)
                ? selectedOptions.filter(selected => selected.value !== option.value)
                : options.filter(item => item === option || selectedOptions.includes(item));
            onChangeMultiple?.(
                newOptions.map(item => item.value),
                newOptions
            );
            return;
        }

        onChange?.(option.value, option);
        setIsOpen(false);
    };

    const hasSelection = multiple ? selectedOptions.length > 0 : !!selectedOption;

    const renderSelectedContent = () => {
        if (multiple) {
            return renderValues
                ? renderValues(selectedOptions)
                : selectedOptions.map(option => option.label).join(', ');
        }

        return selectedOption && renderOption ? renderOption(selectedOption) : selectedOption?.label;
    };

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className={clsx(styles.root, className)}>
            {label && (
                <Label
                    error={error}
                    required={required}
                >
                    {label}
                </Label>
            )}

            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                <div
                    className={styles.selectWrapper}
                    ref={selectRef}
                >
                    <div
                        className={clsx(
                            styles.select,
                            {
                                [styles.selectOpen]: isOpen,
                                [styles.selectFilled]: hasSelection,
                                [styles.selectError]: error,
                                [styles.selectDisabled]: disabled
                            },
                            triggerClassName
                        )}
                        onClick={handleToggle}
                    >
                        <div className={styles.selectContent}>
                            {hasSelection ? (
                                renderSelectedContent()
                            ) : (
                                <span className={styles.placeholder}>{placeholder}</span>
                            )}
                        </div>

                        <FontAwesomeIcon
                            icon={icon || faChevronDown}
                            className={clsx(styles.icon, {
                                [styles.iconOpen]: isOpen
                            })}
                        />
                    </div>

                    {isOpen && (
                        <div className={styles.dropdown}>
                            {options.map(option => (
                                <div
                                    key={option.value}
                                    className={clsx(styles.option, {
                                        [styles.optionSelected]: isOptionSelected(option),
                                        [styles.optionMultiple]: multiple
                                    })}
                                    onClick={() => handleOptionClick(option)}
                                >
                                    {multiple && (
                                        <FontAwesomeIcon
                                            icon={isOptionSelected(option) ? faSquareCheck : faSquare}
                                            className={styles.optionCheckbox}
                                        />
                                    )}
                                    {renderOption ? renderOption(option) : option.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </ClickAwayListener>
        </div>
    );
};

export default Select;
